const { omit } = require('lodash');
const { dayjs } = require('./date');
const { LicenseInterval, OrderItemType, DiscountType, PaymentStatus } = require('../prisma/types');
const { MODULE_LIMIT_SETTING_LABEL } = require('../constants/settings');
const { round } = require('../constants/math');
const { visitLimitOptions } = require('../constants/visitLimits');
const { EUR } = require('../constants/currencies');
const { availableAddons } = require('../constants/license');
const { currencyFormat } = require('./formatter');
const { getModuleLimitSetting, getLicenseIntervalDays, getLicenseEndDate, isLimitInPriceOptions, formatLicenseLimit } = require('./license');

function addonIncluded({ addon, license }) {
  if (license?.settings[addon]) {
    return true;
  }
  return false;
}

function getDiscountCalculatedAmount(amount, type, total) {
  if (total <= 0) {
    return 0;
  }

  return Math.min(type === DiscountType.Percentage ? round(total * (amount / 100)) : amount, total);
}

function getModuleLicensePeriodPrice(prices, interval, currency) {
  if (!prices) {
    return 0;
  }

  if (interval === LicenseInterval.Yearly && prices?.yearlyPrice) {
    return parseFloat(prices.yearlyPrice[currency] || 0);
  }

  let price = interval === LicenseInterval.Trial ? 0 : parseFloat(prices.monthlyPrice?.[currency] || 0);
  if (price && interval === LicenseInterval.Yearly) {
    price = price * 12 * 0.8;
  }

  return price;
}

function getCalculatedOrder(order) {
  let subtotal = 0;
  const items = order.items.map(i => {
    const item = normalizeOrderItemForCalculation(i);
    if (item.price != null) {
      let amount = item.price * item.quantity;
      if (item.discount) {
        amount -= getDiscountCalculatedAmount(item.discount, item.discountType, amount);
      }
      subtotal += amount;
      return {
        ...item,
        amount,
      };
    }
    return item;
  });
  const discounts = [...order.discounts || []];

  if (order.discount) {
    discounts.push({
      title: 'Discount',
      description: order.discountType === DiscountType.Percentage ? `${order.discount}%` : '',
      amount: order.discount,
      type: order.discountType,
    });
  }

  let amount = subtotal;

  if (order.discounts?.length > 0) {
    for (const discount of order.discounts) {
      discount.calculatedAmount = getDiscountCalculatedAmount(discount.amount, discount.type, amount);
      amount -= discount.calculatedAmount;
    }
  }

  return {
    ...order,
    items,
    subtotal,
    discounts,
    amount,
  };
}

function createLegacyLicenseOrder({ settings, interval, license, startDate, endDate }) {
  const visitLimit = parseInt(settings.find(s => s.key === 'matomoMonthlyVisitLimit')?.value || 0, 10);
  const prices = license?.settings?.[interval === LicenseInterval.Yearly ? 'yearlyPrices' : 'monthlyPrices']?.find(p => p.matomoMonthlyVisitLimit.toString() === visitLimit.toString());
  const quantity = interval === LicenseInterval.Yearly ? 12 : 1;

  const items = [];

  if (license) {
    const { title } = license;
    let description;
    const vistLimitLabel = visitLimitOptions.find(o => o.value === visitLimit)?.label;
    if (vistLimitLabel) {
      description = `${vistLimitLabel} visits/month`;
    }

    items.push({
      title,
      description,
      price: prices?.analytics ? parseFloat(prices?.analytics) : undefined,
      type: OrderItemType.License,
      quantity,
      limit: visitLimit,
      relationId: license.id,
      startDate,
      endDate,
    });
  }

  for (const { value: addon, label } of availableAddons) {
    if (addonIncluded({ addon, license })) {
      items.push({ title: `${label} (included)`, amount: 0, quantity: 1, type: OrderItemType.Addon, relationId: addon });
    } else if (settings.find(s => s.key === addon)?.value) {
      items.push({ title: label, amount: parseFloat(prices?.[addon] || 0), type: OrderItemType.Addon, quantity: 1, relationId: addon });
    }
  }

  return items;
}

function getOrderPeriodDays(startDate, endDate) {
  if (!startDate || !endDate) {
    return 0;
  }

  return dayjs(endDate).diff(dayjs(startDate), 'day') + 1;
}

function getPeriodOverlapDays(periodAStart, periodAEnd, periodBStart, periodBEnd) {
  if (!periodAStart || !periodAEnd || !periodBStart || !periodBEnd) {
    return 0;
  }

  const overlapStart = dayjs(periodAStart).isAfter(dayjs(periodBStart)) ? dayjs(periodAStart) : dayjs(periodBStart);
  const overlapEnd = dayjs(periodAEnd).isBefore(dayjs(periodBEnd)) ? dayjs(periodAEnd) : dayjs(periodBEnd);

  if (overlapEnd.isBefore(overlapStart, 'day')) {
    return 0;
  }

  return overlapEnd.diff(overlapStart, 'day') + 1;
}

function getPaidOrders(activeLicense) {
  return (activeLicense?.orders || []).filter(
    order => order.amount != null && order.amount > 0 && [PaymentStatus.Paid, PaymentStatus.Invoice].includes(order.paymentStatus),
  );
}

function getOverlappingPaidOrders(activeLicense, upgradePeriodStart, upgradePeriodEnd) {
  return getPaidOrders(activeLicense).filter((order) => {
    if (!order.startDate || !order.endDate) {
      return false;
    }

    return getPeriodOverlapDays(upgradePeriodStart, upgradePeriodEnd, order.startDate, order.endDate) > 0;
  });
}

function getMidTermChangePeriod({ activeLicense, startDate, interval, endDate }) {
  if (!activeLicense?.endDate || !startDate) {
    return {
      upgradePeriodStart: startDate,
      upgradePeriodEnd: null,
      termDays: 0,
      remainingDays: 0,
      overlappingOrders: [],
    };
  }

  const upgradePeriodStart = startDate;
  let upgradePeriodEnd = activeLicense.endDate;
  const termDays = getLicenseIntervalDays(interval ?? activeLicense?.interval) || 0;

  const newLicenseEndDate = endDate ?? (interval && interval !== activeLicense.interval
    ? getLicenseEndDate(startDate, interval)
    : null);

  if (newLicenseEndDate && dayjs(newLicenseEndDate).isBefore(dayjs(activeLicense.endDate), 'day')) {
    upgradePeriodEnd = newLicenseEndDate;
  }

  const upgradePeriodDays = getOrderPeriodDays(upgradePeriodStart, upgradePeriodEnd);
  const remainingDays = Math.max(0, Math.min(termDays || upgradePeriodDays, upgradePeriodDays));
  const overlappingOrders = getOverlappingPaidOrders(activeLicense, upgradePeriodStart, upgradePeriodEnd);

  return {
    upgradePeriodStart,
    upgradePeriodEnd,
    termDays,
    remainingDays,
    overlappingOrders,
  };
}

function getModuleForRelationId(relationId, { licenses, activeLicense }) {
  const license = licenses?.find(l => l.id === relationId);

  if (license?.module) {
    return license.module;
  }

  const moduleLicense = activeLicense?.modules?.find(m => m.id === relationId);

  return moduleLicense?.module || null;
}

function getPreviousOrderItemsForModules(previousOrder, creditModules, { licenses, activeLicense }) {
  const creditModuleSet = new Set(creditModules);

  return (previousOrder.items || []).filter((item) => {
    if (item.type !== OrderItemType.License || !item.relationId) {
      return false;
    }

    const module = getModuleForRelationId(item.relationId, { licenses, activeLicense });

    return module && creditModuleSet.has(module);
  });
}

function getScaledPreviousOrderDiscounts(previousOrder, matchingSubtotal, fullSubtotal) {
  const discounts = getPreviousOrderDiscounts(previousOrder);

  if (!fullSubtotal || matchingSubtotal >= fullSubtotal) {
    return discounts;
  }

  const ratio = matchingSubtotal / fullSubtotal;

  return discounts.map(discount => ({
    ...discount,
    amount: discount.type === DiscountType.Percentage ? discount.amount : round(discount.amount * ratio),
  }));
}

function getPreviousOrderDiscounts(order) {
  const discounts = (order.discounts || []).map(({ title, description, amount, type }) => ({
    title,
    description,
    amount,
    type,
  }));

  if (order.discount) {
    discounts.push({
      title: 'Discount',
      description: order.discountType === DiscountType.Percentage ? `${order.discount}%` : '',
      amount: order.discount,
      type: order.discountType,
    });
  }

  return discounts;
}

function normalizeOrderItemForCalculation(item) {
  return {
    ...item,
    price: item.price ?? item.amount ?? 0,
    quantity: item.quantity ?? 1,
  };
}

function getMidTermModuleCreditAmount({ activeLicense, module, licenses, startDate, midTermPeriod }) {
  const period = midTermPeriod ?? getMidTermChangePeriod({ activeLicense, startDate });
  const { overlappingOrders, upgradePeriodStart, upgradePeriodEnd } = period;

  if (!module || !startDate || !upgradePeriodEnd || !overlappingOrders?.length) {
    return 0;
  }

  let credit = 0;

  for (const previousOrder of overlappingOrders) {
    const matchingItems = getPreviousOrderItemsForModules(previousOrder, [module], { licenses, activeLicense });

    if (matchingItems.length) {
      const overlapDays = getPeriodOverlapDays(
        upgradePeriodStart,
        upgradePeriodEnd,
        previousOrder.startDate,
        previousOrder.endDate,
      );
      const orderTermDays = getOrderPeriodDays(previousOrder.startDate, previousOrder.endDate);

      if (overlapDays && orderTermDays) {
        const fullOrder = getCalculatedOrder(previousOrder);
        const matchingSubtotal = getCalculatedOrder({
          items: matchingItems.map(normalizeOrderItemForCalculation),
        }).subtotal;

        const { amount: paidAmount } = getCalculatedOrder({
          items: matchingItems.map(normalizeOrderItemForCalculation),
          discounts: getScaledPreviousOrderDiscounts(previousOrder, matchingSubtotal, fullOrder.subtotal),
        });

        if (paidAmount) {
          credit += (paidAmount * overlapDays) / orderTermDays;
        }
      }
    }
  }

  return round(credit);
}

function createModuleLicenseOrder({ modules, settings, interval, currency, licenses, midTermPeriod, activeLicense, startDate }) {
  const items = [];
  const addons = {};

  for (const licenseId of modules) {
    const license = licenses.find(l => l.id === licenseId);

    if (license) {
      const existingModule = activeLicense?.modules.find(m => m.module === license?.module);
      const setting = getModuleLimitSetting(license?.module, license?.level);
      const limit = parseInt(settings?.find(s => s.key === setting)?.value || 0, 10);
      const existingLimit = activeLicense?.settings[setting];

      const prices = license?.settings.prices?.find(p => p[setting] === limit);

      let price = getModuleLicensePeriodPrice(prices, interval, currency);
      let description = `${formatLicenseLimit(limit)} ${MODULE_LIMIT_SETTING_LABEL[setting]}`;

      let isLimitChange = false;

      if (midTermPeriod?.remainingDays) {
        const { termDays, remainingDays } = midTermPeriod;
        const upgradePeriodDays = remainingDays;
        const fractionOfPeriod = termDays ? Math.min(1, remainingDays / termDays) : 1;
        const newLicenseRemainingPrice = round(price * fractionOfPeriod);

        if (existingModule) {
          if (existingLimit === limit) {
            price = 0;
          } else {
            isLimitChange = true;
            const credit = getMidTermModuleCreditAmount({
              activeLicense,
              module: license.module,
              licenses,
              startDate,
              midTermPeriod,
            });
            price = Math.max(0, round(newLicenseRemainingPrice - credit));
            if (isLimitInPriceOptions(license, setting, existingLimit)) {
              description = `${limit > existingLimit ? 'Upgrade' : 'Downgrade'} from ${formatLicenseLimit(existingLimit)} to ${formatLicenseLimit(limit)} ${MODULE_LIMIT_SETTING_LABEL[setting]} (${upgradePeriodDays} days)`;
            } else {
              description = `${formatLicenseLimit(limit)} ${MODULE_LIMIT_SETTING_LABEL[setting]} (${upgradePeriodDays} days)`;
            }
          }
        } else {
          price = newLicenseRemainingPrice;
        }
      }

      if (price !== 0 || isLimitChange) {
        items.push({
          title: license.title,
          description,
          price: round(price),
          quantity: 1,
          limit,
          type: OrderItemType.License,
          relationId: license.id,
        });
      }

      for (const addonId of settings?.find(s => s.key === `${license.module}-addons`)?.value || []) {
        const addon = licenses.find(l => l.id === addonId);

        if (addon) {
          if (!addons[addon.id]) {
            const addonAmount = addon.settings.monthlySinglePrice[currency];
            addons[addon.id] = ({ title: addon.title, amount: addonAmount, description: license.title, type: OrderItemType.Addon, relationId: addon.id });
            addons[addon.id].quantity = 1;
          } else {
            addons[addon.id].price = addon.settings.monthlyMultiplePrice[currency];
            addons[addon.id].description += `, ${license.title}`;
          }
        }
      }
    }
  }

  items.push(...Object.values(addons));

  return { items };
}

function createOrder({
  modules,
  settings,
  interval,
  startDate,
  endDate,
  currency = EUR,
  license,
  licenses,
  dataSource,
  discount,
  discountType = DiscountType.Percentage,
  discounts = [],
  activeLicense,
  bundleDiscounts,
}) {
  let midTermPeriod;

  if (activeLicense && startDate.isBefore(activeLicense.endDate) && activeLicense.interval !== LicenseInterval.Trial) {
    if (interval === activeLicense.interval) {
      endDate = activeLicense.endDate;
    } else if (!endDate) {
      endDate = getLicenseEndDate(startDate, interval);
    }

    midTermPeriod = getMidTermChangePeriod({ activeLicense, startDate, interval, endDate });
  }

  if (!endDate) {
    endDate = getLicenseEndDate(startDate, interval);
  }

  const newOrder = {
    dataSource: dataSource.title,
    items: [],
    discounts,
    currency,
    startDate,
    endDate,
  };

  if (discount) {
    newOrder.discounts.push({
      title: 'Discount',
      amount: discount,
      type: discountType,
    });
  }

  if (license) {
    newOrder.items.push(...createLegacyLicenseOrder({ settings, interval, license, startDate, endDate }));
  }

  if (modules && licenses) {
    const { items } = createModuleLicenseOrder({ modules, settings, interval, currency, licenses, midTermPeriod, activeLicense, startDate });
    newOrder.items.push(...items);
    if (bundleDiscounts) {
      const licenseItems = newOrder.items.filter(item => item.type === OrderItemType.License);
      if (licenseItems.length > 0) {
        const licenseLevel = Math.min(...modules.map(m => licenses.find(l => l.id === m)?.level));
        const bundleDiscount = bundleDiscounts.find(bd => bd.licenseLevel === licenseLevel && bd.nrOfProducts === modules.length);
        if (bundleDiscount) {
          const bundleDiscountAmount = licenseItems.reduce((acc, item) => acc + item.price, 0);
          if (bundleDiscountAmount > 0) {
            newOrder.discounts.splice(0, 0, {
              title: `${bundleDiscount.bundleDiscount}% Bundle discount`,
              amount: round(bundleDiscountAmount * (bundleDiscount.bundleDiscount / 100)),
              type: DiscountType.Amount,
            });
          }
        }
      }
    }
  }

  return getCalculatedOrder(newOrder);
}

function createOrderFromConfig(orderConfig) {
  return {
    dataSource: orderConfig.dataSource,
    currency: orderConfig.currency,
    items: orderConfig.items.map(item => omit(item, ['id'])),
    subtotal: orderConfig.subtotal,
    discounts: orderConfig.discounts.map(discount => omit(discount, ['id'])),
    amount: orderConfig.amount,
  };
}

function formattedOrder(order) {
  let note = `<b>Order #${order.orderNumber}</b><br/>`;

  note += `Period: <b>${dayjs(order.startDate).format('YYYY-MM-DD')} - ${dayjs(order.endDate).format('YYYY-MM-DD')}</b><br/>`;

  for (const item of order.items) {
    const labels = [item.title, item.description];
    note += `${labels.filter(label => label).join(' - ')}${item.amount != null ? `: <b>${currencyFormat(item.amount * item.quantity, { currency: order.currency })}</b>` : ''}<br/>`;
  }

  if (order.subtotal) {
    note += `Subtotal: <b>${currencyFormat(order.subtotal, { currency: order.currency })}</b><br/>`;
  }

  if (order.discounts?.length > 0) {
    for (const discount of order.discounts) {
      if (discount.calculatedAmount) {
        const labels = [discount.title, discount.description, discount.type === DiscountType.Percentage && `${discount.amount}%`];
        note += `${labels.filter(label => label).join(' - ')}: <b>${currencyFormat(-discount.calculatedAmount, { currency: order.currency })}</b><br/>`;
      }
    }
  }

  if (order.monthlyAmount) {
    note += `Total / month: <b>${currencyFormat(order.monthlyAmount, { currency: order.currency })}</b><br/>`;
  }

  note += `Total: <b>${currencyFormat(order.amount, { currency: order.currency })}</b><br/>`;

  return note;
}

const fullOrderInfo = `
  id        
  createdAt
  updatedAt
  dataSource
  startDate
  endDate
  orderNumber  
  items {
    id
    relationId
    title
    description
    type
    price
    discount
    discountType
    amount
    quantity
    startDate
    endDate
  }
  discount
  discountType
  discounts {
    id
    title
    description
    amount
    calculatedAmount
    type
  }
  subtotal
  amount
  currency
  paymentType
  paymentStatus         
  paymentDate
  stripeInvoiceId
  pipedriveNoteId
`;

module.exports = {
  createOrder,
  createOrderFromConfig,
  getCalculatedOrder,
  getMidTermModuleCreditAmount,
  getModuleLicensePeriodPrice,
  formattedOrder,
  fullOrderInfo,
};
